import React, { useRef, useEffect } from "react";
import { HiOutlineX, HiOutlineExclamationCircle } from "react-icons/hi";
import api from "@/services/api.js";
import toast from "react-hot-toast";
import { capture } from "@/instrument";
import { useNavigate } from "react-router-dom";
import { useLoading } from "@/context/LoadingContext.jsx";

const AbandonModal = ({ onClose }) => {
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoading();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const modalRef = useRef(null);

  const handleAbandon = async () => {
    try {
      startLoading();
      const res = await api.delete("/admission");
      if (!res.ok) throw res;
      navigate("/");
    } catch (error) {
      capture(error);
      toast.error("Une erreur est survenue lors de l'abandon de l'enregistrement.");
      navigate("/");
    } finally {
      sessionStorage.removeItem("formToken");
      sessionStorage.removeItem("deviceId");
      sessionStorage.removeItem("patientToken");
      localStorage.removeItem("rememberMe");
      stopLoading();
    }
  };

  useEffect(() => {
    const modalElement = modalRef.current;
    if (isMobile === false || modalElement === null) {
      return;
    }

    const touchStartYRef = { value: 0 };

    const handleTouchStart = (event) => {
      touchStartYRef.value = event.touches[0].clientY;
      modalElement.style.transition = "none";
    };

    const handleTouchMove = (event) => {
      const touchCurrentY = event.touches[0].clientY;
      const deltaY = touchCurrentY - touchStartYRef.value;

      if (deltaY > 0) {
        event.preventDefault();
        modalElement.style.transform = `translateY(${deltaY}px)`;
      }
    };

    const handleTouchEnd = (event) => {
      const touchEndY = event.changedTouches[0].clientY;
      const deltaY = touchEndY - touchStartYRef.value;
      const closeThreshold = 100;

      modalElement.style.transition = "transform 0.3s ease-out";

      if (deltaY > closeThreshold) {
        modalElement.style.transform = `translateY(100%)`;
        setTimeout(() => {
          onClose();
        }, 300);
      } else {
        modalElement.style.transform = "translateY(0)";
      }
    };

    modalElement.addEventListener("touchstart", handleTouchStart);
    modalElement.addEventListener("touchmove", handleTouchMove, { passive: false });
    modalElement.addEventListener("touchend", handleTouchEnd);

    return () => {
      modalElement.removeEventListener("touchstart", handleTouchStart);
      modalElement.removeEventListener("touchmove", handleTouchMove);
      modalElement.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isMobile, onClose]);

  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
        <div ref={modalRef} className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up" style={{ transition: "transform 0.3s ease-out", touchAction: "none" }}>
          <div className="bg-white rounded-t-2xl p-6 w-full shadow-xl">
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />

            <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-500">
              <HiOutlineX className="w-4 h-4" />
            </button>

            <div className="text-center mb-6">
              <div className="bg-red-100 rounded-full p-3 h-16 w-16 flex items-center justify-center mx-auto mb-4">
                <HiOutlineExclamationCircle className="h-10 w-10 text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Abandonner l'enregistrement ?</h2>
            </div>

            <div className="mb-6 text-center">
              <p className="text-gray-600 text-sm">Êtes-vous sûr de vouloir abandonner votre enregistrement ? Toutes les informations saisies seront perdues.</p>
            </div>

            <div className="flex flex-col gap-3">
              <button onClick={handleAbandon} className="w-full px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-all duration-200 shadow-md">
                Abandonner
              </button>
              <button onClick={onClose} className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all duration-200">
                Annuler
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm transition-opacity duration-300" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-2xl w-full mx-auto shadow-2xl transition-all duration-300 opacity-100 scale-100">
          <div className="relative p-6">
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Fermer">
              <HiOutlineX className="w-5 h-5" />
            </button>

            <div className="text-center mb-6">
              <div className="bg-red-100 rounded-full p-3 h-16 w-16 flex items-center justify-center mx-auto mb-4">
                <HiOutlineExclamationCircle className="h-10 w-10 text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Abandonner l'enregistrement ?</h2>
              <div className="h-1 w-16 bg-red-500 mx-auto mt-2 rounded-full"></div>
            </div>

            <div className="mb-6 text-center">
              <p className="text-gray-600">Êtes-vous sûr de vouloir abandonner votre enregistrement ? Toutes les informations saisies seront perdues.</p>
            </div>

            <div className="flex justify-center gap-4">
              <button onClick={onClose} className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all duration-200">
                Annuler
              </button>
              <button
                onClick={handleAbandon}
                className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-all duration-200 hover:scale-105 shadow-md"
              >
                Abandonner
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AbandonModal;
